import { Share2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import type { TakeMediaFile } from "@/types"

interface Props {
  body: string
  mediaFiles: TakeMediaFile[]
}

function isImageContentType(contentType?: string | null) {
  return contentType?.startsWith("image/") ?? false
}

function filenameFromUrl(url: string, fallback: string) {
  try {
    const parsed = new URL(url, window.location.origin)
    const segments = parsed.pathname.split("/")
    const last = segments[segments.length - 1]
    return last && last.length > 0 ? decodeURIComponent(last) : fallback
  } catch {
    return fallback
  }
}

async function mediaFilesAsFiles(mediaFiles: TakeMediaFile[]): Promise<File[]> {
  const imageFiles = mediaFiles.filter(
    (file) => file.url && isImageContentType(file.content_type),
  )
  const fetched = await Promise.all(
    imageFiles.map(async (file, index) => {
      const response = await fetch(file.url!)
      if (!response.ok) {
        throw new Error(`Fetch failed with status ${response.status}`)
      }
      const blob = await response.blob()
      const name = filenameFromUrl(file.url!, `image-${index + 1}`)
      return new File([blob], name, { type: blob.type })
    }),
  )
  return fetched
}

export default function ExportActions({ body, mediaFiles }: Props) {
  const [shareError, setShareError] = useState<string | null>(null)
  const [canShareWithFiles, setCanShareWithFiles] = useState(false)

  const visibleMediaFiles = useMemo(
    () =>
      mediaFiles.filter(
        (file) => file.url && isImageContentType(file.content_type),
      ),
    [mediaFiles],
  )

  useEffect(() => {
    if (
      typeof navigator === "undefined" ||
      typeof navigator.canShare !== "function"
    ) {
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const files = await mediaFilesAsFiles(visibleMediaFiles)
        if (cancelled) return
        const payload =
          files.length > 0 ? { files, text: body } : { text: body }
        setCanShareWithFiles(navigator.canShare(payload))
      } catch {
        if (!cancelled) setCanShareWithFiles(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [body, visibleMediaFiles])

  if (!canShareWithFiles) {
    return null
  }

  async function handleShare() {
    setShareError(null)
    try {
      const files = await mediaFilesAsFiles(visibleMediaFiles)
      const payload = files.length > 0 ? { files, text: body } : { text: body }
      await navigator.share(payload)
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return
      }
      setShareError("Sharing didn't work — try copying instead.")
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
        Export
      </p>
      <Button variant="outline" size="sm" onClick={() => void handleShare()}>
        <Share2 className="size-3.5" aria-hidden />
        Share…
      </Button>
      {shareError && (
        <p role="alert" className="text-xs text-red-600">
          {shareError}
        </p>
      )}
    </div>
  )
}
